/**
 * OnboardingView — PHASE-A 首次体验（一本书还没有任何剧集时的全屏入口）。
 *
 * 单一入口：一个大号 prompt 方框。可以直接描述让 AI 生成，也可以把一部小说
 * （.txt/.md）拖进方框 / 点击附件按钮上传 —— 服务端分章后 agent 改编第一章。
 * 生成中展示流式文字；失败时顶部弹出错误条。可选「加载示例剧本」次要入口。
 *
 * 纯展示 + 受控输入；真正的网络/SSE 由容器（App）注入。全部走 i18n + cream token。
 */

import { useRef, useState, type ChangeEvent, type DragEvent, type KeyboardEvent, type ReactElement } from "react";
import { ArrowUp, BookOpen, Paperclip, Sparkles, Wand2 } from "lucide-react";
import { Button, IconButton, Textarea, Spinner } from "../../ui/index.js";
import { LanguageToggle } from "../../ui/LanguageToggle.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { COMMON } from "../../i18n/common.js";
import "./OnboardingView.css";

const S = {
  eyebrow: { zh: "你的故事，从这里开始", en: "Your story starts here" },
  invite: {
    zh: "写下一段故事。AI 轻声协作，让文字成为可以参与的世界。",
    en: "Write a passage. AI works alongside you to make your story playable.",
  },
  placeholder: {
    zh: "例如：一所漂浮在云端的魔法学院，主角在入学典礼上遇见沉默寡言的天才学长……",
    en: "e.g. A magic academy adrift in the clouds; at the opening ceremony the lead meets a quiet, brilliant senior…",
  },
  ariaPrompt: { zh: "描述你想写的故事", en: "Describe the story you want" },
  create: { zh: "开始创作", en: "Start writing" },
  creating: { zh: "正在创作中，请稍候…", en: "Working on it…" },
  enterHint: { zh: "写下或粘贴正文 · 随时开始", en: "Write or paste your prose · Start anytime" },
  streamHead: { zh: "正在创作…", en: "Writing…" },
  startersLabel: { zh: "试试这些起手式", en: "Try one of these" },
  // Adapt-from-novel — folded into the composer box (drag-drop / attach).
  adaptHint: {
    zh: "或者您可以拖拽并添加文件，直接改编现有的小说",
    en: "Or drag in / attach a file to adapt an existing novel directly",
  },
  attachAria: { zh: "上传小说文件（.txt / .md）", en: "Attach a novel file (.txt / .md)" },
  dropActive: { zh: "松开以改编这个文件", en: "Drop to adapt this file" },
  adaptReading: { zh: "正在读取文件…", en: "Reading file…" },
  adaptEmpty: { zh: "这个文件是空的。", en: "That file is empty." },
  example: { zh: "加载示例剧本，先体验编辑器", en: "Load the example script to explore the editor" },
} satisfies Record<string, Entry>;

export interface OnboardingViewProps {
  /** 提交 prompt 文本给 AI 生成（容器负责真正的生成调用）。 */
  readonly onSend: (text: string) => void;
  readonly onStartWriting?: (text: string) => void;
  /** 上传/拖入的小说文本 → 服务端改编（容器负责 SSE）。 */
  readonly onAdaptNovel?: (novelText: string, fileName?: string) => void;
  /** 生成进行中：锁输入、展示流式文字与「正在创作…」。 */
  readonly busy: boolean;
  /** 生成中实时回流的文字（可选）。 */
  readonly streamingText?: string;
  /** 起手式建议（点击即以该文案开始生成）。 */
  readonly starters: readonly string[];
  /** 点击某个起手式 chip。 */
  readonly onStarter: (text: string) => void;
  /** 可选：加载示例剧本，不经过 AI 直接进入工作台。 */
  readonly onLoadExample?: () => void;
  /** 失败时展示的错误文案。 */
  readonly error?: string | null;
  /** When present, show why final AI generation may require sign-in. */
  readonly aiDisabledReason?: string | null;
}

export function OnboardingView(props: OnboardingViewProps): ReactElement {
  const {
    onSend,
    onStartWriting,
    onAdaptNovel,
    busy,
    streamingText,
    starters,
    onStarter,
    onLoadExample,
    error,
    aiDisabledReason,
  } = props;
  const tr = useTr();
  const [text, setText] = useState("");
  const [reading, setReading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const canSend = text.trim().length > 0 && !busy;
  const inputDisabled = busy;

  const submit = (): void => {
    if (!canSend) return;
    (onStartWriting ?? onSend)(text.trim());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  const adaptFile = async (file: File): Promise<void> => {
    if (!onAdaptNovel || busy) return;
    setReading(true);
    try {
      const content = await file.text();
      if (content.trim().length === 0) {
        window.alert(tr(S.adaptEmpty));
        return;
      }
      onAdaptNovel(content, file.name);
    } finally {
      setReading(false);
    }
  };

  const onPickFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) await adaptFile(file);
  };

  const onDragOver = (e: DragEvent<HTMLFormElement>): void => {
    if (!onAdaptNovel || busy) return;
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = (): void => setDragActive(false);
  const onDrop = async (e: DragEvent<HTMLFormElement>): Promise<void> => {
    if (!onAdaptNovel) return;
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await adaptFile(file);
  };

  const hasStream = busy && Boolean(streamingText && streamingText.length > 0);

  return (
    <main className="lw-onboarding" aria-label={tr(S.create)}>
      <div className="lw-onboarding__aura" aria-hidden="true" />
      <LanguageToggle className="lw-onboarding__lang" />

      <section className="lw-onboarding__hero">
        <span className="lw-onboarding__eyebrow">
          <Sparkles size={14} aria-hidden="true" />
          {tr(S.eyebrow)}
        </span>

        <h1 className="lw-onboarding__wordmark">{tr(COMMON.appName)}</h1>

        <p className="lw-onboarding__invite">{tr(S.invite)}</p>

        {error ? (
          <div className="lw-onboarding__error" role="alert">
            {error}
          </div>
        ) : null}
        {aiDisabledReason ? (
          <div className="lw-onboarding__notice" role="status">
            {aiDisabledReason}
          </div>
        ) : null}

        <form
          className="lw-onboarding__composer"
          data-drag-active={dragActive || undefined}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={(e) => void onDrop(e)}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Textarea
            className="lw-onboarding__field"
            aria-label={tr(S.ariaPrompt)}
            placeholder={tr(S.placeholder)}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={inputDisabled}
            rows={4}
          />

          <div className="lw-onboarding__composer-foot">
            <span className="lw-onboarding__hint">{busy ? tr(S.creating) : tr(S.enterHint)}</span>
            <div className="lw-onboarding__composer-actions">
              {onAdaptNovel ? (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".txt,.md,text/plain,text/markdown"
                    className="lw-onboarding__file"
                    onChange={(e) => void onPickFile(e)}
                  />
                  <IconButton
                    label={tr(S.attachAria)}
                    size="md"
                    disabled={inputDisabled || reading}
                    onClick={() => fileRef.current?.click()}
                  >
                    <Paperclip size={17} aria-hidden="true" />
                  </IconButton>
                </>
              ) : null}
              <Button
                type="submit"
                size="lg"
                variant="primary"
                loading={busy}
                disabled={!canSend}
                trailingIcon={<ArrowUp size={18} aria-hidden="true" />}
              >
                {tr(S.create)}
              </Button>
            </div>
          </div>

          {dragActive ? <div className="lw-onboarding__drop" aria-hidden="true">{tr(S.dropActive)}</div> : null}
        </form>

        {onAdaptNovel ? (
          <button
            type="button"
            className="lw-onboarding__adapt-hint"
            disabled={busy || reading}
            onClick={() => fileRef.current?.click()}
          >
            <Paperclip size={16} aria-hidden="true" />
            {reading ? tr(S.adaptReading) : tr(S.adaptHint)}
          </button>
        ) : null}

        {hasStream ? (
          <div className="lw-onboarding__stream" aria-live="polite">
            <div className="lw-onboarding__stream-head">
              <Spinner size="sm" />
              <span>{tr(S.streamHead)}</span>
            </div>
            <p className="lw-onboarding__stream-text">{streamingText}</p>
          </div>
        ) : starters.length > 0 ? (
          <div className="lw-onboarding__starters">
            <span className="lw-onboarding__starters-label">
              <Wand2 size={13} aria-hidden="true" />
              {tr(S.startersLabel)}
            </span>
            <ul className="lw-onboarding__chips">
              {starters.map((s) => (
                <li key={s}>
                  <button type="button" className="lw-onboarding__chip" disabled={busy} onClick={() => onStarter(s)}>
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {onStartWriting && !busy ? <div className="lw-onboarding__secondary">
          <Button variant="ghost" onClick={() => onStartWriting("")}>{tr({ zh: "从空白页开始", en: "Start with a blank page" })}</Button>
          <Button variant="ghost" disabled={!text.trim()} onClick={() => onSend(text.trim())}>{tr({ zh: "和 AI 构思", en: "Brainstorm with AI" })}</Button>
        </div> : null}
        {onLoadExample && !busy ? (
          <div className="lw-onboarding__secondary">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onLoadExample}
              leadingIcon={<BookOpen size={15} aria-hidden="true" />}
            >
              {tr(S.example)}
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  );
}
